import Rox from 'rox-node';
import { default as nodeFetch } from 'node-fetch';
import { EventSource, type FetchLike } from 'eventsource';
import { getLogger } from './logger';
import { promiseTimeout } from '../utils';
import { ROLLOUT_KEY as PROD_ROLLOUT_KEY, IS_ON_PREM, GATEWAY_URL } from './config';

const DEV_ROLLOUT_KEY = '5b5566db9601aa6484276524';
const logger = getLogger('FeatureFlagsService');

// IS_UNIT_TEST = disable rollout if code run in unit test mode to ignore mocha process stuck on running
const USE_FEATURE_FLAGS = !IS_ON_PREM && !process.env.IS_UNIT_TEST && !GATEWAY_URL;

const FORCE_FETCH_TIMEOUT_MS = 20_000; // rollout sometimes takes up to 15 seconds to load
const SEC_IN_DAY = 60 * 60 * 24;
const LAB_FEATURE_FLAG_VALUES = ['labs', 'disabled', 'enabled'] as const;

type TCustomFetch = (url: string, options: any) => Promise<nodeFetch.Response>;
type TCustomEventSource = (url: string) => EventSource;

/**
 * From CloudBees support, as of 2025-03-26:
 *
 * `fetchFunction` was made for a specific customer recently, and it's true that we didn't document any of this, but it's something  that it will be in future docs.
 *
 * The same is for `eventSourceImpl`, though it has been there for some time.
 *
 * Available as of `rox-node@5.4.12`.
 */
type RoxSetupOptions = Rox.RoxSetupOptions & {
    fetchFunction?: TCustomFetch;
    eventSourceImpl?: TCustomEventSource;
};

class LabFeatureFlag extends Rox.RoxString {
    // RoxString is a direct replacement of the old Variant class
    constructor(initialValue = 'disabled') {
        super(initialValue, LAB_FEATURE_FLAG_VALUES);
    }

    getValue() {
        const value = super.getValue() as (typeof LAB_FEATURE_FLAG_VALUES)[number];
        if (!LAB_FEATURE_FLAG_VALUES.includes(value)) {
            logger.warn('unexpected value for lab feature flag. Falling back to value "labs"', {
                featureFlagName: this.name,
                value,
            });
            return 'labs';
        }

        return value;
    }
}

class FeatureFlagsService {
    public readonly flags;
    constructor() {
        this.flags = {
            useNewWSCLI: new Rox.Flag(),
            useSafariWebdriverVisibilityChecks: new Rox.Flag(),
            useClickimVisibilityChecks: new Rox.Flag(),
            runGetElementCodeInAut: new Rox.Flag(),
            enableFrameSwitchOptimization: new Rox.Flag(),
            maximumJsResultSize: new Rox.RoxNumber(2000 * 1024),
            skipFileInputClicks: new Rox.Flag(),
            errorMessageOnBadNetwork: new Rox.Flag(true),
            warnOnBadNetwork: new Rox.Flag(false),
            overrideAzureStorageUrl: new Rox.Flag(),
            autoSaveDownloadFileFireFox: new Rox.Flag(true),
            safariSelectOptionDispatchEventOnSelectElement: new Rox.Flag(true),
            experimentalPreCodeCompilation: new Rox.Flag(true),
            /** Enables using top level await inside custom actions */
            experimentalAsyncCustomCode: new Rox.Flag(),
            useSameBrowserForMultiTests: new LabFeatureFlag('labs'),
            highSpeedMode: new LabFeatureFlag(),
            usePortedHtml5DragDrop: new Rox.Flag(),
            testNamesToBeforeSuiteHook: new Rox.Flag(),
            addCustomCapabilities: new Rox.RoxString('{}'),
            addCustomLTtunnelNTLMOptions: new Rox.RoxString('{}'),
            LTNetworkCapabilities: new Rox.Flag(),
            downloadToBase64: new Rox.Flag(),
            publicGridURL: new Rox.RoxString('public-grid.testim.io'),
            allowAppFromDeviceRuns: new Rox.Flag(true),
            unsupportedCapsFields: new Rox.RoxString('[]'),
            dontFocusOnInit: new Rox.Flag(),
            /** @deprecated its not longer needed as we moved to Selenium 4 */
            retryOnSeleniumHubError: new Rox.Flag(),
            enableLambdaTestTunnelNTLM: new Rox.Flag(), // Adds support to NTLM authorization on LambdaTest tunnel
            enableLambdaTestTunnelSkipUpgrade: new Rox.Flag(), // If LT released pre-release tunnel binary with fix for a bug, this flag will stop the script from downloading the latest stable binary
            tdcHsPreventNetworkCapture: new Rox.Flag(),
            enableBatchTDC: new Rox.Flag(),
            clearRunnerFileCache: new Rox.Flag(),
            forceTurboModeIfNeeded: new Rox.Flag(),
            /* @deprecated was never in use */
            redisSlotManager: new Rox.Flag(),
            tunnelBinaryNewVersion: new Rox.Flag(false),
            encryptedCredentials: new Rox.Flag(),
            computePackagePathFromPackageJsonExports: new Rox.Flag(),
            sendExtensionOnEdgeNonLinux: new Rox.Flag(),
            reuseCliPkgInstallFolder: new Rox.Flag(),
            getPublicIpFrom3rdPartyServices: new Rox.Flag(),
            chromedriverCdnBinariesUrl: new Rox.RoxString(''), // If needed, supply an alternative URL for downloading the chromedriver binary
            sfdcHybridCompany: new Rox.Flag(),
            allowFixingAppIdStructure: new Rox.Flag(),
            enableAppium2BrowserStack: new Rox.Flag(),
            increaseTDCRequestTimeout: new Rox.Flag(),
            accessTokenExpiryMargin: new Rox.RoxNumber(300 * 1000), // How much time before the token expires should we refresh it
            useMV3Extension: new Rox.Flag(),
            testimHeadlessExtensionDownloadUrl: new Rox.RoxString('', []),
            applyMobileRestrictionFromDate: new Rox.RoxNumber(1723507200000), // Restrict mobile trial plans for companies created after specified date --- Tue Aug 13 2024 00:00:00
            removeMobileTrialRestrictions: new Rox.Flag(),
            appiumConnectionRetryCount: new Rox.RoxNumber(0),
            // 120000 ms = 2 minutes
            appiumConnectionRequestTimeout: new Rox.RoxNumber(120000),
            DFBrowserVersion: new Rox.RoxString('latest', ['latest', 'latest-1', 'latest-2']),
            DFAcceptInsecureOrigins: new Rox.RoxString('http://jsbin.testim.io'),
            enableFixForCustomExtensionForLT: new Rox.Flag(false),
            enableTestPlanAggregation: new Rox.Flag(),
            secrets: new Rox.Flag(),
            chromeArgsDisableFeatures: new Rox.RoxString(''), // comma separated list of additional features to disable in chrome using arg --disable-features
            // We are migrating away from ngrok, when enabled this FF will create a tunnel using ngrok instead of CF.
            useNgrokTunnel: new Rox.Flag(false),
            // Logger provider toggles (both enabled by default, can be disabled via FF)
            // TODO: Coralogix cleanup - Remove disableCoralogixLogger flag once migration to BetterStack is complete
            disableCoralogixLogger: new Rox.Flag(false),
            disableBetterStackLogger: new Rox.Flag(false),
        };
        Rox.register('default', this.flags);
    }

    setProjectId(projectId: string) {
        Rox.setCustomStringProperty('projectId', projectId);
    }

    setProjectType(projectType: string) {
        Rox.setCustomStringProperty('projectType', projectType);
    }

    setCompanyProductType(productType: string) {
        Rox.setCustomStringProperty('productType', productType);
    }

    setCompanyId(companyId: string) {
        Rox.setCustomStringProperty('companyId', companyId);
    }

    setPlanType(planType: string) {
        Rox.setCustomStringProperty('planType', planType);
    }

    setIsPOC(isPOC: boolean) {
        Rox.setCustomBooleanProperty('isPOC', isPOC);
    }

    setIsStartUp(isStartUp: boolean) {
        Rox.setCustomBooleanProperty('isStartUp', isStartUp);
    }

    setRunnerMode(mode: string) {
        Rox.setCustomStringProperty('runnerMode', mode);
    }

    setRunnerEnvironment(mode: string) {
        Rox.setCustomStringProperty('runnerEnvironment', mode);
    }

    /**
     * Fetches the feature flags and configuration data from CloudBees.
     * Accepts a `proxy` parameter so that the request can be sent through a proxy server - problem solved in `TES-24246`.
     */
    async fetch({ proxy }: { proxy?: string }) {
        let proxyAgent = undefined;
        let customFetch: TCustomFetch | undefined = undefined;
        let customES: TCustomEventSource | undefined = undefined;

        if (!USE_FEATURE_FLAGS) {
            return undefined;
        }

        /**
         * As instructed by CloudBees, if we want proxy support:
         * 1. a custom `fetchFunction` must be provided,
         * 2. a custom `eventSourceImpl` must be provided.
         * 3. The `eventSourceImpl` must use the custom `fetchFunction` to have requests go through the `proxy`.
         */
        if (proxy) {
            proxyAgent = require('https-proxy-agent').HttpsProxyAgent;
            const proxyAgentInstance = proxy ? new proxyAgent(proxy) : undefined;

            customFetch = (url: string, options: any) => {
                return nodeFetch(url, { ...options, agent: proxyAgentInstance });
            };

            customES = function (url: string) {
                return new EventSource(url, { fetch: customFetch as unknown as FetchLike });
            };
        }

        const opts: RoxSetupOptions = {
            fetchIntervalInSec: SEC_IN_DAY, // we don't actually want to refresh feature flags in the CLI,
            fetchFunction: customFetch,
            eventSourceImpl: customES,
        };

        // TODO: TES-17223 - set rollout key base on env
        // const ROLLOUT_KEY = (global.meta?.IS_RELEASE || process.env.IS_CI_RUN) ? PROD_ROLLOUT_KEY : DEV_ROLLOUT_KEY;

        // To work against dev FF, you need to set the key to the dev key, until TES-17223 is solved.
        return promiseTimeout(Rox.setup(PROD_ROLLOUT_KEY, opts), FORCE_FETCH_TIMEOUT_MS).catch((err) =>
            logger.error('failed to get feature flag status', err),
        );
    }
}

export type LabFeatureFlagType = LabFeatureFlag;
export const featureFlagsService = new FeatureFlagsService();
